import { EM_DASH, FieldKey } from "~/pages/Compliance/ProposedChangesModal/constants";
import {
  computeDocumentChangeFlags,
  createDocumentMetaDataSignature,
  formatAddress,
  formatControllers,
  formatOwners,
  isDifferent,
} from "~/pages/Compliance/ProposedChangesModal/utils";
import { formatDocumentType } from "~/pages/Compliance/utils/documentUtils";
import { computeDimmedMatches, makeRow, type SectionArgs } from "./index";
import type { DimmedMatches } from "../types";
import type { ProfileData } from "~/pages/Compliance/ProposedChangesModal/types";

const toDocData = (
  docs: ProfileData["documents"] | undefined,
  flagRef?: Map<number, boolean>,
) =>
  docs?.map((d) => ({
    id: d.id,
    name: d.name,
    s3Link: d.s3Link,
    documentPurpose: d.documentPurpose,
    status: d.status,
    metadata: d.metadata,
    hasMetaDataChange: flagRef?.get(d.id) ?? false,
  }));

const formatDomicile = (addresses: ProfileData["addresses"] | null | undefined): string => {
  if (!addresses || addresses.length === 0) return EM_DASH;
  const primary = addresses.find((addr) => addr.isPrimaryDomicile) || addresses[0];
  return primary?.country?.name || EM_DASH;
};

// Documents are cloned per profile version, so ids differ even when the file
// is semantically identical. Match on name (rows are already scoped to one
// baseType) plus the actual metadata content signature — a boolean
// "changed vs current" flag would collide when two versions differ from
// current in different ways.
const docsSignature = (docs: ProfileData["documents"] | undefined): string =>
  [...(docs ?? [])]
    .map((d) => `${d.name}|${createDocumentMetaDataSignature(d)}`)
    .sort()
    .join("||");

const dimmedForDocs = (
  curDocs: ProfileData["documents"] | undefined,
  pubDocs: ProfileData["documents"] | undefined,
  propDocs: ProfileData["documents"] | undefined,
  hasPublished: boolean,
): DimmedMatches =>
  computeDimmedMatches(
    docsSignature(curDocs),
    docsSignature(pubDocs),
    docsSignature(propDocs),
    hasPublished,
  );

export const appendAddressRow = ({
  rows,
  currentProfile,
  publishedProfile,
  proposedProfile,
  hasPublished,
  isJoint,
}: SectionArgs) => {
  // Joint profiles only persist a domicile country, not a full address.
  const format = isJoint ? formatDomicile : formatAddress;
  const cur = format(currentProfile.addresses);
  const pub = publishedProfile ? format(publishedProfile.addresses) : EM_DASH;
  const prop = format(proposedProfile.addresses);
  if (cur === prop && (!hasPublished || cur === pub)) return;
  rows.push(
    makeRow({
      rowKey: FieldKey.Address,
      fieldKey: FieldKey.Address,
      field: isJoint ? "Domicile" : "Address",
      current: cur,
      recentlyPublished: pub,
      proposed: prop,
      hasPublished,
    }),
  );
};

export const appendDocumentRows = ({
  rows,
  currentProfile,
  publishedProfile,
  proposedProfile,
  hasPublished,
}: SectionArgs) => {
  const propDiff = isDifferent.documents(currentProfile.documents, proposedProfile.documents);
  const pubDiff = publishedProfile
    ? isDifferent.documents(currentProfile.documents, publishedProfile.documents)
    : { isDifferent: false, differentBaseTypes: new Set<string>() };

  const baseTypes = new Set<string>([
    ...propDiff.differentBaseTypes,
    ...pubDiff.differentBaseTypes,
  ]);
  if (baseTypes.size === 0) return;

  baseTypes.forEach((baseType) => {
    const curDocs = currentProfile.documents?.filter(
      (d) => (d.documentPurpose?.baseType || "other") === baseType,
    );
    const pubDocs = publishedProfile?.documents?.filter(
      (d) => (d.documentPurpose?.baseType || "other") === baseType,
    );
    const propDocs = proposedProfile.documents?.filter(
      (d) => (d.documentPurpose?.baseType || "other") === baseType,
    );

    const curData = toDocData(curDocs);
    const pubData = toDocData(pubDocs, computeDocumentChangeFlags(curDocs, pubDocs));
    const propData = toDocData(propDocs, computeDocumentChangeFlags(curDocs, propDocs));

    rows.push(
      makeRow(
        {
          rowKey: `${FieldKey.Document}:${baseType}`,
          fieldKey: FieldKey.Document,
          field: formatDocumentType(baseType),
          current: curDocs && curDocs.length > 0 ? "" : EM_DASH,
          recentlyPublished: hasPublished && pubDocs && pubDocs.length > 0 ? "" : EM_DASH,
          proposed: propDocs && propDocs.length > 0 ? "" : EM_DASH,
          hasPublished,
          isDocument: true,
          baseType,
          currentDocs: curData,
          publishedDocs: pubData,
          proposedDocs: propData,
        },
        dimmedForDocs(curDocs, pubDocs, propDocs, hasPublished),
      ),
    );
  });
};

export const appendOwnersRow = ({
  rows,
  currentProfile,
  publishedProfile,
  proposedProfile,
  hasPublished,
  isIndividual,
}: SectionArgs) => {
  const propId = proposedProfile.sourceProfileId ?? proposedProfile.id;
  const pubId = publishedProfile?.sourceProfileId ?? publishedProfile?.id;
  const differsProp = isDifferent.owners(
    currentProfile.beneficialOwners,
    proposedProfile.beneficialOwners,
    currentProfile.id,
    propId,
  );
  const differsPub =
    hasPublished && publishedProfile
      ? isDifferent.owners(
          currentProfile.beneficialOwners,
          publishedProfile.beneficialOwners,
          currentProfile.id,
          pubId,
        )
      : false;
  if (!differsProp && !differsPub) return;

  rows.push(
    makeRow({
      rowKey: FieldKey.Owners,
      fieldKey: FieldKey.Owners,
      field: isIndividual ? "Owner" : "Owners",
      current: formatOwners(currentProfile.beneficialOwners),
      recentlyPublished: hasPublished
        ? formatOwners(publishedProfile?.beneficialOwners)
        : EM_DASH,
      proposed: formatOwners(proposedProfile.beneficialOwners),
      hasPublished,
    }),
  );
};

export const appendControllersRow = ({
  rows,
  currentProfile,
  publishedProfile,
  proposedProfile,
  hasPublished,
  isIndividual,
}: SectionArgs) => {
  const propId = proposedProfile.sourceProfileId ?? proposedProfile.id;
  const pubId = publishedProfile?.sourceProfileId ?? publishedProfile?.id;
  const differsProp = isDifferent.controllers(
    currentProfile.controllingPersons,
    proposedProfile.controllingPersons,
    currentProfile.id,
    propId,
  );
  const differsPub =
    hasPublished && publishedProfile
      ? isDifferent.controllers(
          currentProfile.controllingPersons,
          publishedProfile.controllingPersons,
          currentProfile.id,
          pubId,
        )
      : false;
  if (!differsProp && !differsPub) return;

  rows.push(
    makeRow({
      rowKey: FieldKey.Controllers,
      fieldKey: FieldKey.Controllers,
      field: isIndividual ? "Controller" : "Controllers",
      current: formatControllers(currentProfile.controllingPersons),
      recentlyPublished: hasPublished
        ? formatControllers(publishedProfile?.controllingPersons)
        : EM_DASH,
      proposed: formatControllers(proposedProfile.controllingPersons),
      hasPublished,
    }),
  );
};
